import { Text, toast, type TextLabelStyle } from "@carbon6/c6-frontend-design";
import { createElement, Fragment, type ReactNode } from "react";
import { hatcheryTextModes } from "@/app/theme";
import { MAX_WINDOWS, WindowStackManager } from "@/components/modal";

/**
 * 앱 스코프 전체화면 창 스택이다. 제네릭 WindowStackManager 에 앱의 kind 집합과 cap 안내(toast)를 추가한다.
 * 매니저는 순수하다(kind 는 불투명 문자열이고, 안내가 없다). 그래서 이 모듈이 앱 정책(창 종류·초과 시
 * 토스트)을 소유한다. 소비처는 이 모듈의 windowStack 싱글턴과 openWindow 헬퍼만 쓴다.
 */

// 앱이 스택에 추가하는 창 종류다. 호스트 레지스트리가 이 값으로 콘텐츠를 매핑한다.
// Eval 상세·비교·병합은 evaluationResults 창 안의 로컬 드릴다운(RightSidePanel)이다.
// 세션 비교는 스택 밖의 RightSidePanel 이다. 그래서 둘 다 여기에 없다.
export type WindowKind =
  | "datasetDetail"
  | "trainingSession"
  | "trainingSubmit"
  | "diagnosticSubmit"
  | "datasetUpload"
  | "evaluationResults"
  | "scheduleOccurrences"
  | "sessionDatasets";

type WindowStackHotData = {
  windowStack?: WindowStackManager;
};

// React 재마운트와 Vite 모듈 교체 뒤에도 매니저를 유지한다.
const hotData = import.meta.hot?.data as WindowStackHotData | undefined;
export const windowStack = hotData?.windowStack ?? new WindowStackManager();
if (hotData !== undefined) {
  hotData.windowStack = windowStack;
}

const windowTaskbarLabelStyle = {
  prefix: {
    as: "strong",
    style: { color: "var(--c6-color-text-accent)" },
  },
} satisfies TextLabelStyle;

export function windowTaskbarTextLabel(prefix: string, title: string): ReactNode {
  return createElement(
    Text,
    {
      variant: hatcheryTextModes["caption-bold"],
      labelStyle: windowTaskbarLabelStyle,
    },
    "<prefix>" + prefix + "</prefix> ",
    createElement(Fragment, null, title),
  );
}

// Cap 초과로 창을 열지 못했을 때의 사용자 안내다(앱 locale 정책). 매니저는 null 만 반환하고,
// 이 모듈이 안내한다.
export function notifyWindowCapReached(): void {
  toast.info(
    `전체 화면 창은 최대 ${MAX_WINDOWS}개까지 열 수 있습니다. 기존 창을 닫고 다시 시도해 주세요.`,
  );
}
